import { ReactFlowProvider, useReactFlow, useStore } from "@xyflow/react";
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { Canvas } from "@/components/canvas/canvas";
import { getDefinition, resolveImageFilename } from "@/lib/catalog";
import { planExampleFrame } from "@/lib/hero-frame";
import { loadImageContentBounds } from "@/lib/product-image-fit";
import { useCableLengthVisibility } from "@/lib/settings/cable-length-visibility";
import { useCableSizeVisibility } from "@/lib/settings/cable-size-visibility";
import { useCableVdropVisibility } from "@/lib/settings/cable-vdrop-visibility";
import { useFuseVisibility } from "@/lib/settings/fuse-visibility";
import { useProductImageVisibility } from "@/lib/settings/product-image-visibility";
import { useSystemStore } from "@/lib/store";
import { catalogImageUrl } from "@/lib/supabase/client";
import type { System } from "@/lib/types";

type CaptureStatus = "loading" | "ready" | "error";

/** A clean photograph of the actual canvas, used to refresh static example cards.
 * This only changes presentation; the authored system and cable routes are intact. */
export function WiringSchematicCapture({ system }: { system: System }) {
  const root = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<CaptureStatus>("loading");

  useLayoutEffect(() => {
    const mode = useSystemStore.getState().mode;
    const settings = [useCableLengthVisibility, useCableSizeVisibility, useCableVdropVisibility, useFuseVisibility];
    const visible = settings.map(setting => setting.getState().visible);
    const productImages = useProductImageVisibility.getState().visible;
    // These are isolated, in-memory overrides, never saved editor preferences.
    settings.forEach(setting => setting.setState({ visible: false }));
    useProductImageVisibility.setState({ visible: true });
    useSystemStore.getState().loadSystem(system, { preserveLayout: true });
    useSystemStore.setState({ mode: "edit" });
    setLoaded(true);
    return () => {
      settings.forEach((setting, index) => setting.setState({ visible: visible[index] }));
      useProductImageVisibility.setState({ visible: productImages });
      useSystemStore.setState({ mode });
    };
  }, [system]);

  return <div ref={root} className="lp lp-figure h-full w-full overflow-hidden bg-[var(--lp-paper)]"
    role="img" aria-label={`${system.name} — Crimp schematic`}
    data-schematic-capture data-capture-ready={status === "ready"} data-capture-status={status}>
    {loaded && <div className="pointer-events-none h-full w-full" inert>
      <ReactFlowProvider>
        <Canvas viewOnly hideCableLabels embedded autoFit={false} showControls={false}
          showMetricsOnTouch={false} showBatteryDetails={false} elementsFocusable={false} minZoom={0.05} />
        <CaptureFrame root={root} onStatusChange={setStatus} />
      </ReactFlowProvider>
    </div>}
  </div>;
}

function CaptureFrame({ root, onStatusChange }: {
  root: RefObject<HTMLDivElement | null>;
  onStatusChange: (status: CaptureStatus) => void;
}) {
  const { fitBounds } = useReactFlow();
  // Canvas keeps dimensions out of its controlled domain nodes. Read Flow's
  // measured internals, rather than waiting for dimensions to be copied back.
  const nodesInitialized = useStore(state => state.nodeLookup.size > 0 &&
    Array.from(state.nodeLookup.values()).every(node =>
      node.measured.width !== undefined && node.measured.height !== undefined && node.internals.handleBounds !== undefined));
  const paneWidth = useStore(state => state.width);
  const paneHeight = useStore(state => state.height);
  const system = useSystemStore(state => state.system);

  useEffect(() => {
    onStatusChange("loading");
    if (!nodesInitialized || !paneWidth || !paneHeight) return;
    const photoFilenames = system.nodes.flatMap(node => {
      if (node.kind !== "component") return [];
      const definition = getDefinition(node.definitionId, node.customDefinition);
      if (!definition || (definition.renderVariant ?? "card") !== "card") return [];
      const filename = resolveImageFilename(definition);
      return filename ? [filename] : [];
    });
    // A missing catalog host silently renders text cards. A successful capture
    // must include every photo the actual card renderer is supposed to show.
    if (photoFilenames.some(filename => !catalogImageUrl(filename))) {
      onStatusChange("error");
      return;
    }
    const frame = planExampleFrame(system, { paneWidth, paneHeight, maxZoom: 1.2, padding: 30 });
    if (!frame) return;
    let cancelled = false;
    const nextPaint = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    void (async () => {
      await fitBounds(frame, { padding: 0, duration: 0 });
      await document.fonts?.ready;
      // Fitting first brings every lazy product photo into the viewport. Wait
      // for both the photo and its shared transparent-padding analysis, then
      // let React apply the final image fit before declaring capture readiness.
      await nextPaint();
      const images = Array.from(root.current?.querySelectorAll<HTMLImageElement>(".component-card--image img") ?? []);
      if (images.length !== photoFilenames.length) throw new Error("Expected product photos are missing");
      await Promise.all(images.map(async image => {
        await image.decode();
        if (!image.naturalWidth) throw new Error("Product photo failed to load");
        await loadImageContentBounds(image.currentSrc || image.src);
      }));
      await nextPaint();
      await nextPaint();
      if (!cancelled) onStatusChange("ready");
    })().catch(() => {
      if (!cancelled) onStatusChange("error");
    });
    return () => { cancelled = true; };
  }, [fitBounds, nodesInitialized, onStatusChange, paneWidth, paneHeight, root, system]);

  return null;
}
